/* =========================================================================
   ws-perf/perf-metrics.ts — Web Vitals instrumentation (FCP, LCP, CLS, INP).
   Workstream A: Performance & Code Splitting.
   ========================================================================= */

export interface WebVitalsReport {
  name: "FCP" | "LCP" | "CLS" | "INP" | "TTFB";
  value: number;
  rating: "good" | "needs-improvement" | "poor";
}

function getRating(name: string, value: number): WebVitalsReport["rating"] {
  switch (name) {
    case "FCP":
      return value <= 1800
        ? "good"
        : value <= 3000
          ? "needs-improvement"
          : "poor";
    case "LCP":
      return value <= 2500
        ? "good"
        : value <= 4000
          ? "needs-improvement"
          : "poor";
    case "CLS":
      return value <= 0.1
        ? "good"
        : value <= 0.25
          ? "needs-improvement"
          : "poor";
    case "INP":
      return value <= 200
        ? "good"
        : value <= 500
          ? "needs-improvement"
          : "poor";
    case "TTFB":
      return value <= 800
        ? "good"
        : value <= 1800
          ? "needs-improvement"
          : "poor";
    default:
      return "poor";
  }
}

export function observeWebVitals(
  onReport: (metric: WebVitalsReport) => void,
): void {
  if (typeof PerformanceObserver === "undefined") return;

  const observe = (
    entryType: string,
    nameMap: Record<string, WebVitalsReport["name"]>,
  ) => {
    if (
      PerformanceObserver.supportedEntryTypes &&
      !PerformanceObserver.supportedEntryTypes.includes(entryType)
    ) {
      return;
    }
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const name = nameMap[entry.entryType] ?? nameMap[entry.name];
          if (!name) continue;
          const value =
            entryType === "layout-shift"
              ? (entry as unknown as { value: number }).value
              : (entry as unknown as { startTime: number }).startTime;
          onReport({ name, value, rating: getRating(name, value) });
        }
      });
      observer.observe({ type: entryType as never, buffered: true });
    } catch {
      // Observer type not supported
    }
  };

  observe("paint", { "first-contentful-paint": "FCP" });
  observe("web-vitals", { LCP: "LCP", INP: "INP" });
  observe("layout-shift", { "layout-shift": "CLS" });

  // TTFB from navigation timing
  const nav = performance.getEntriesByType("navigation")[0] as
    PerformanceNavigationTiming | undefined;
  if (nav) {
    onReport({
      name: "TTFB",
      value: nav.responseStart,
      rating: getRating("TTFB", nav.responseStart),
    });
  }
}
